/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import * as Yup from "yup";
import { InputFieldProps, FormFieldsProps } from "../../types/InputProps";
import * as yup from "yup";
import dayjs from "dayjs";

type YupBoolean = Yup.BooleanSchema<
  boolean | undefined,
  Yup.AnyObject,
  boolean | undefined
>;
type YupString = Yup.StringSchema<
  string | undefined,
  Yup.AnyObject,
  string | undefined
>;

const generateValidations = (field: InputFieldProps) => {
  // @ts-ignore
  let schema: Yup.AnySchema = Yup[field.typeValue || "string"]();

  if (field.validations) {
    for (const rule of field.validations) {
      switch (rule.type) {
        case "isTrue":
          schema = (schema as YupBoolean).isTrue(rule.message);
          break;
        case "isEmail":
          schema = (schema as YupString).email(rule.message);
          break;
        case "minLength":
          schema = (schema as YupString).min(
            rule?.value as number,
            rule.message,
          );
          break;
        case "oneOf":
          schema = (schema as YupString).oneOf(
            [Yup.ref((rule as any).ref)],
            rule.message,
          );
          break;
        case "minArray":
          schema = (schema as Yup.AnyObject).min(1, rule.message);
          break;
        case "regex":
          schema = (schema as Yup.AnyObject).matches(rule.regexp, rule.message);
          break;
        default:
          schema = schema.required(rule.message);
          break;
      }
    }
  }

  return schema;
};

export const getAllFields = (allForms: FormFieldsProps[]) => {
  const allFields: InputFieldProps[] = [];
  allForms.forEach((formFields) => {
    formFields.fields.map((a) => allFields.push(a));
  });
  return allFields;
};

export const isSameOrAfter = (
  end: dayjs.ConfigType,
  start: dayjs.ConfigType,
): boolean => {
  const date1 = dayjs(end);
  const date2 = dayjs(start);
  return (
    date1.isValid() &&
    date2.isValid() &&
    (date1.isSame(date2) || date1.isAfter(date2))
  );
};

function getYupTypeForField(field: InputFieldProps) {
  switch (field.typeValue) {
    case "date":
      return Yup.date()
        .nullable()
        .transform((_value, originalValue) =>
          originalValue === "" || !dayjs(originalValue).isValid()
            ? null
            : dayjs(originalValue).toDate(),
        );
    case "array":
      return Yup.array();
    default:
      return Yup.string();
  }
}

export const getInputs = <T>(fields: InputFieldProps[], initialValue?: any) => {
  const initialValues: { [key: string]: any } = {};

  const validationsFields: { [key: string]: any } = {};

  for (const field of fields) {
    initialValues[field.name] = field.value; // Assign value from schema

    if (field.type === "date-range-picker") {
      const sKey = field.meta.start.key || "start";
      const eKey = field.meta.end.key || "end";
      const sKeyLabel = field.meta.start.label || "Start date";
      const eKeyLabel = field.meta.end.label || "End date";

      const toNullableDate = (value: any) =>
        dayjs(value).isValid() ? dayjs(value).toDate() : null;
      validationsFields[field.name] = yup.object({
        [sKey]: Yup.date()
          .nullable()
          .transform((_, val) => toNullableDate(val)),
        [eKey]: Yup.date()
          .nullable()
          .transform((_, val) => toNullableDate(val))
          .test(
            "endAfterStart",
            `${eKeyLabel} can't be before ${sKeyLabel}`,
            function (endDate) {
              const startDate = this.parent[sKey];
              return (
                !startDate || !endDate || isSameOrAfter(endDate, startDate)
              );
            },
          ),
      });
      initialValues[field.name] = {
        [sKey]: initialValue?.[field.name]?.[sKey] ?? null,
        [eKey]: initialValue?.[field.name]?.[eKey] ?? null,
      };
      continue;
    }

    initialValues[field.name] =
      field.type === "multi-select" && !field.value
        ? []
        : field.type === "datepicker"
          ? field.value || null
          : field.value;

    const baseSchema = getYupTypeForField(field) as Yup.AnySchema;
    if (field.dependency) {
      validationsFields[field.name] = baseSchema.when(field.dependency.ref, {
        is: (...values: any[]) => {
          const depValue = field.dependency!.value;
          if (typeof depValue === "function") {
            return depValue(values);
          }
          if (Array.isArray(depValue)) {
            return depValue.includes(values[0]);
          }
          return values[0] === depValue;
        },
        then: () => {
          const schema = fieldValidations(field);
          return field.typeValue === "date"
            ? (schema as Yup.DateSchema<Date | null>).nullable()
            : schema;
        },
        otherwise: (schema: any) => schema.nullable().notRequired(),
      });
    } else if (field.validator || field.validations) {
      validationsFields[field.name] = fieldValidations(field);
    }
  }

  return {
    validationSchema: Yup.object({ ...validationsFields }),
    initialValues: initialValues as T,
    inputs: fields,
  };
};

function fieldValidations(field: InputFieldProps) {
  return field.validator ?? generateValidations(field);
}
